import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import { v4 as uuidv4 } from 'uuid'
import { useQueryClient } from 'react-query'
import {
  useQuestionPutQuery,
  useQuestionPostQuery,
} from '../../Queries/QuestionQuery'
import { htmlToString, isQuizStarted } from '../../utils'
import { useStyles } from './AddFillInTheBlankStyles'
import ConfirmBox from '../common/ConfirmBox'
import {
  useGetAllQuizzesForAQuestion,
  useUpdateQuestionForQuizzes,
} from '../../Queries/QuizQuery'
import EditFillInTheBlank from './EditFillInTheBlank'
import FillInTheBlankTile from './FillInTheBlankTile'

function AddFillInTheBlank({
  questionTypeFromParent,
  handleQuestionTypeChange,
  questions,
  quizType,
  codingQuestionSwitch,
  question,
  questionIndex,
  addQuestion,
  setAddQuestion,
  reload,
  setReload,
  quizId,
  quizStartTime,
  isPublished,
  save,
  setSave,
  setCancelAddQuestion,
  setErrorSaveQuestionSnackbar,
  setSaveQuestionSnackbar,
  postQuestionMapping,
  deleteMappingQuestion,
  refetchGetQueryById,
  refetchGetQuizQuestionQueryById,
  deleteQuestion,
  isWriteAllowed,
  setErrorMessage,
  setLocalQuestion,
  localQuestion,
  setTextEditorQuestion,
  textEditorQuestion,
  setEditViewQuestion,
  editViewQuestion,
  setTruthValue,
  deleteQuestionId,
  setDeleteQuestionId,
  editOngoing,
  setEditOngoing,
  editQuestion,
  setEditQuestion,
}) {
  const queryClient = useQueryClient()
  const classes = useStyles()
  const questionMcqType = 'fill-in-blanks'
  const [openDialog, setOpenDialog] = useState(false)

  const [errors, setErrors] = useState({
    emptyMarks: false,
    marksNotValid: false,
    marksLessThanZero: false,
    emptyQuestion: false,
  })

  const [affectedQuizes, setAffectedQuizes] = useState([])
  const [selectedQuizzes, setSelectedQuizzes] = useState([])
  const [showQuizesListModal, setShowQuizesListModal] = useState(false)

  const editQuestionQuery = useQuestionPutQuery(
    quizId,
    localQuestion?.id,
    questionMcqType,
    {
      onSuccess: () => {
        setEditQuestion(NaN)
        setSaveQuestionSnackbar(true)
        setReload(reload + 1)
        refetchGetQueryById()
        refetchGetQuizQuestionQueryById()
        setLocalQuestion({})
        setEditViewQuestion({})
        setTextEditorQuestion({ question: '<p></p>' })
        setTruthValue(true)
        setErrorMessage('Question Saved Successfully!')
      },
      onError: () => {
        setErrorMessage('Failed')
        setErrorSaveQuestionSnackbar(true)
      },
    }
  )

  const postQuestion = useQuestionPostQuery({
    staleTime: 0,
    onSuccess: async (successResponse) => {
      const id = successResponse?.data?.data?.id
      const questionDetailsObj = {
        questionId: id,
        type: 'fill-in-blanks',
      }
      postQuestionMapping.mutate(questionDetailsObj)
      setSaveQuestionSnackbar(true)
      setAddQuestion(false)
      setReload(reload + 1)
      setSave(false)
      await refetchGetQueryById()
      await refetchGetQuizQuestionQueryById()
    },
    onError: () => {
      setErrorSaveQuestionSnackbar(true)
      setSave(false)
      setErrorMessage('Failed')
    },
  })

  const updateQuestionsInQuizzes = useUpdateQuestionForQuizzes({
    onSuccess: () => {
      setEditQuestion(NaN)
      setShowQuizesListModal(false)
      setErrorMessage('Question Saved Successfully!')
      setTruthValue(true)
      setSaveQuestionSnackbar(true)
      setAddQuestion(false)
      setReload(reload + 1)
      refetchGetQueryById()
      refetchGetQuizQuestionQueryById()
      queryClient.invalidateQueries('quizGetQueryById')
    },
  })

  const postNewQuestion = useQuestionPostQuery({
    staleTime: 0,
    retry: false,
    onSuccess: (successResponse) => {
      const id = successResponse?.data?.data?.id
      const newQuestionId = id
      updateQuestionsInQuizzes.mutate({
        oldQuestionId: localQuestion?.id,
        newQuestionId,
        type: 'fill-in-blanks',
        marks: localQuestion?.marks,
        quizIDs: selectedQuizzes.map((quiz) => quiz?.quizID),
      })
    },
  })

  const { refetch: refetchQuizzesForQuestion } = useGetAllQuizzesForAQuestion(
    localQuestion?.id,
    {
      enabled: false,
      onSuccess: (response) => {
        const filteredQuizzes = response?.filter((quiz) => {
          return quiz?.status === 'DRAFT' || quiz?.status === 'UPCOMING'
        })
        setAffectedQuizes(filteredQuizzes || [])
      },
    }
  )

  useEffect(() => {
    if (!Number.isNaN(editQuestion)) {
      refetchQuizzesForQuestion()
    }
  }, [editQuestion])

  useEffect(() => {
    if (question && !addQuestion) return

    const isEditingFillInBlanks =
      editViewQuestion?.id !== undefined &&
      editViewQuestion?.type === 'fillInBlanks'

    const value = {
      id: isEditingFillInBlanks ? editViewQuestion.id : uuidv4(),
      question: isEditingFillInBlanks ? editViewQuestion.question : ' ',
      acceptedAnswers: isEditingFillInBlanks
        ? editViewQuestion.acceptedAnswers
        : [{ id: `Added${uuidv4()}`, value: '' }],
      marks: isEditingFillInBlanks ? editViewQuestion.marks : 2,
      type: isEditingFillInBlanks ? editViewQuestion.type : 'fillInBlanks',
      isRandomize: isEditingFillInBlanks ? editViewQuestion.isRandomize : false,
      quizID: quizId,
    }

    setLocalQuestion(value)
    setTextEditorQuestion((prev) => ({ ...prev, question: value.question }))
    setEditViewQuestion(value)
  }, [question])

  const marksValidation = (value) => {
    if (!value) {
      setErrors((prev) => ({
        ...prev,
        emptyMarks: true,
        marksLessThanZero: false,
        marksNotValid: false,
      }))
      return
    }

    if (!/^-?\d+\.?\d*$/.test(value)) {
      setErrors((prev) => ({
        ...prev,
        emptyMarks: false,
        marksNotValid: true,
        marksLessThanZero: false,
      }))
      return
    }

    setErrors((prev) => ({
      ...prev,
      emptyMarks: false,
      marksNotValid: false,
      marksLessThanZero: Number(value) <= 0,
    }))
  }

  const handleEdit = (event, setting, selectedChoiceId) => {
    const dummyQuestion = JSON.parse(JSON.stringify(localQuestion))

    if (setting === 'answers') {
      const modifiedChoices = dummyQuestion.acceptedAnswers.map((choice) => {
        if (choice?.id === selectedChoiceId)
          return { ...choice, value: event.target.value }
        return choice
      })
      dummyQuestion.acceptedAnswers = modifiedChoices
    }

    if (setting === 'marks') {
      marksValidation(event.target.value)
      dummyQuestion.marks = event.target.value
    }

    setLocalQuestion(dummyQuestion) // instead of setting local Question call API
  }

  const updateQuestion = () => {
    setLocalQuestion((prev) => ({
      ...prev,
      question: textEditorQuestion.question,
    }))
  }

  const handleAddAnswer = (setting) => {
    const dummyQuestion = JSON.parse(JSON.stringify(localQuestion))
    const index = dummyQuestion?.acceptedAnswers?.length
    if (setting === 'Add') {
      dummyQuestion.acceptedAnswers
        .splice(index + 1, 0, {
          id: `Added${uuidv4()}`,
          value: '',
        })
        .join()
    }
    setLocalQuestion(dummyQuestion)
  }

  const removeId = (dummyQuestion) => {
    const modified = dummyQuestion?.acceptedAnswers?.map(
      ({ id, ...remainingProperties }) => {
        let returnObj
        if (typeof id === 'string' && id.includes('Added')) {
          returnObj = { ...remainingProperties }
        } else {
          returnObj = { id, ...remainingProperties }
        }
        return returnObj
      }
    )
    const temp = dummyQuestion
    temp.acceptedAnswers = modified
    return temp
  }

  const countBlanks = (fibQuestion) => {
    // YOU WILL NEVER KNOW WHY I NAME IT fibQuestion instead of question MUHAHAHHA (sry)
    const matches = fibQuestion.match(/_+/g)
    return matches ? matches.length : 0
  }

  const handleSave = async (type = '') => {
    const dummyLocalQuestion = JSON.parse(JSON.stringify(localQuestion))
    dummyLocalQuestion.question = textEditorQuestion.question
    dummyLocalQuestion.marks = parseInt(dummyLocalQuestion.marks, 10)
    dummyLocalQuestion.acceptedAnswers =
      dummyLocalQuestion.acceptedAnswers.filter((a) => a !== '')

    // Validate question not empty
    if (htmlToString(dummyLocalQuestion.question).trim() === '') {
      setErrors((prev) => ({ ...prev, emptyQuestion: true }))
      if (addQuestion) setSave(false)
      return
    }

    setErrors((prev) => ({ ...prev, emptyQuestion: false }))

    // Validate blanks exist and match answer count
    const blankCount = countBlanks(dummyLocalQuestion.question)
    if (blankCount === 0) {
      setErrorMessage(
        'Fill in the blanks should contain at least one blank (____)'
      )
      return
    }

    if (dummyLocalQuestion.acceptedAnswers.length !== blankCount) {
      setErrorMessage(
        `You have ${blankCount} blank(s) but ${dummyLocalQuestion.acceptedAnswers.length} choice(s). They must match.`
      )
      return
    }

    // Validate no empty answers
    if (dummyLocalQuestion.acceptedAnswers.some((a) => a.value.trim() === '')) {
      setErrorMessage('All answers must be filled in')
      return
    }

    dummyLocalQuestion.quizID = quizId
    dummyLocalQuestion.type = 'fill-in-blanks'

    if (type === 'duplicateEdit') {
      await postNewQuestion.mutateAsync(removeId(dummyLocalQuestion))
      setEditOngoing(false)
      return
    }

    if (!addQuestion) {
      if (dummyLocalQuestion.isEditable === false) {
        deleteMappingQuestion.mutate({
          quizId,
          questionId: dummyLocalQuestion?.id,
        })
        delete dummyLocalQuestion.id
        postQuestion.mutate(dummyLocalQuestion)
      } else {
        editQuestionQuery.mutate(removeId(dummyLocalQuestion))
      }
    } else {
      delete dummyLocalQuestion.id
      dummyLocalQuestion.acceptedAnswers =
        dummyLocalQuestion.acceptedAnswers.map(({ value }) => ({ value }))
      postQuestion.mutate(dummyLocalQuestion)
    }

    setEditOngoing(false)
  }

  useEffect(() => {
    if (save && addQuestion) {
      handleSave()
    }
  }, [save, addQuestion])

  const handleDelete = () => {
    setOpenDialog(true)
    setDeleteQuestionId(question?.id)
  }

  const handleClick = () => {
    deleteQuestion.mutate({
      quizId,
      questionId: deleteQuestionId,
      questionType: questionMcqType,
    })
    setOpenDialog(false)
    setDeleteQuestionId(undefined)
  }
  const handleClose = () => {
    setOpenDialog(false)
  }

  const handleRemoveAnswer = (choiceIndex) => {
    const dummyQuestion = JSON.parse(JSON.stringify(localQuestion))
    const filterAcceptedAnswers = dummyQuestion?.acceptedAnswers?.filter(
      (x) => x?.id !== choiceIndex
    )
    dummyQuestion.acceptedAnswers = filterAcceptedAnswers
    setLocalQuestion(dummyQuestion)
  }

  const handleCancel = () => {
    setEditOngoing(false)
    setEditQuestion(NaN)
    setLocalQuestion(question)
    setAddQuestion(false)
  }

  const handleCancelAddTf = () => {
    setEditOngoing(false)
    setCancelAddQuestion(false)
    setAddQuestion(false)
  }

  const handleChange = () => {
    if (editOngoing) {
      setErrorMessage('Please finish the previous edit ')
    } else {
      setLocalQuestion(question)
      setTextEditorQuestion({ question: question?.question })
      setEditQuestion(questionIndex)
      setEditOngoing(true)
    }
  }

  const updateQuestionInSelectedQuizzes = async () => {
    updateQuestion()
    const dummyLocalQuestion = JSON.parse(JSON.stringify(localQuestion))
    delete dummyLocalQuestion.id
    dummyLocalQuestion.quizID = quizId
    await postNewQuestion.mutateAsync(removeId(dummyLocalQuestion))
  }

  return (
    <Grid item container xs={12}>
      {editQuestion === questionIndex || addQuestion ? (
        <EditFillInTheBlank
          question={question}
          addQuestion={addQuestion}
          questions={questions}
          questionIndex={questionIndex}
          localQuestion={localQuestion}
          errors={errors}
          handleEdit={handleEdit}
          isQuizStarted={isQuizStarted}
          quizStartTime={quizStartTime}
          isPublished={isPublished}
          questionTypeFromParent={questionTypeFromParent}
          handleQuestionTypeChange={handleQuestionTypeChange}
          quizType={quizType}
          codingQuestionSwitch={codingQuestionSwitch}
          textEditorQuestion={textEditorQuestion}
          setTextEditorQuestion={setTextEditorQuestion}
          editQuestionQuery={editQuestionQuery}
          postQuestion={postQuestion}
          editQuestion={editQuestion}
          handleCancel={handleCancel}
          handleCancelAddTf={handleCancelAddTf}
          handleSave={handleSave}
          showQuizesListModal={showQuizesListModal}
          setShowQuizesListModal={setShowQuizesListModal}
          affectedQuizes={affectedQuizes}
          updateQuestionInSelectedQuizzes={updateQuestionInSelectedQuizzes}
          setSelectedQuizzes={setSelectedQuizzes}
          selectedQuizzes={selectedQuizzes}
          quizId={quizId}
          handleAddAnswer={handleAddAnswer}
          handleRemoveAnswer={handleRemoveAnswer}
          setLocalQuestion={setLocalQuestion}
          editViewQuestion={editViewQuestion}
        />
      ) : (
        <FillInTheBlankTile
          questionIndex={questionIndex}
          editQuestion={editQuestion}
          addQuestion={addQuestion}
          question={question}
          setEditQuestion={setEditQuestion}
          isPublished={isPublished}
          isQuizStarted={isQuizStarted}
          quizStartTime={quizStartTime}
          deleteQuestion={deleteQuestion}
          handleDelete={handleDelete}
          isWriteAllowed={isWriteAllowed}
          handleChange={handleChange}
        />
      )}
      <ConfirmBox
        isOpenDialog={openDialog}
        handleClose={handleClose}
        handleClick={handleClick}
        boxTitle="Delete Question"
        boxDescription="Are you sure you want to delete this question?"
        styles={classes}
        buttonName="Delete"
      />
    </Grid>
  )
}

export default AddFillInTheBlank
